import { axiosPrivate, BASE_URL } from "./axios";

/**
 * Desktop monitoring: work sessions, screenshots, recordings, settings, and
 * per-employee capture-mode overrides.
 *
 * Everything here is admin-only on the backend (`high_potit` team) except where
 * noted. Employees have no endpoint to view their own recordings by design.
 */
export const monitoringService = {
  // ── Sessions ──────────────────────────────────────────────────────────────
  getSessions: async ({
    employeeId,
    startDate,
    endDate,
    status,
    page = 1,
    limit = 25,
  } = {}) => {
    const params = { page, limit };
    if (employeeId) params.employeeId = employeeId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (status && status !== "all") params.status = status;
    const res = await axiosPrivate.get("/api/monitoring/admin/sessions", {
      params,
    });
    return res.data;
  },

  /** Currently open sessions, for the live panel. Totals are computed live. */
  getActiveSessions: async ({ employeeId } = {}) => {
    const params = {};
    if (employeeId) params.employeeId = employeeId;
    const res = await axiosPrivate.get(
      "/api/monitoring/admin/sessions/active",
      { params },
    );
    return res.data;
  },

  getSessionDetail: async (sessionId) => {
    const res = await axiosPrivate.get(
      `/api/monitoring/admin/sessions/${sessionId}`,
    );
    return res.data;
  },

  forceEndSession: async (sessionId, reason) => {
    const res = await axiosPrivate.post(
      `/api/monitoring/admin/sessions/${sessionId}/force-end`,
      { reason },
    );
    return res.data;
  },

  // ── Recordings (video) ────────────────────────────────────────────────────
  getRecordings: async ({
    employeeId,
    sessionId,
    startDate,
    endDate,
    page = 1,
    limit = 50,
  } = {}) => {
    const params = { page, limit };
    if (employeeId) params.employeeId = employeeId;
    if (sessionId) params.sessionId = sessionId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const res = await axiosPrivate.get("/api/monitoring/admin/recordings", {
      params,
    });
    return res.data;
  },

  /**
   * Mint a short-lived token for one recording. Also writes an audit-log entry
   * server-side — viewing someone's screen is always recorded.
   */
  createPlaybackToken: async (recordingId, ttlSeconds) => {
    const res = await axiosPrivate.post(
      `/api/monitoring/admin/recordings/${recordingId}/playback-token`,
      ttlSeconds ? { ttlSeconds } : {},
    );
    return res.data;
  },

  /**
   * Absolute url for a <video src>. The endpoint is Range-capable, so the browser
   * streams and seeks natively instead of downloading the whole file first.
   */
  getStreamUrl: (recordingId, token) =>
    `${BASE_URL}/api/monitoring/recordings/${recordingId}/stream?t=${encodeURIComponent(token)}`,

  // ── Screenshots ───────────────────────────────────────────────────────────
  getScreenshots: async ({
    employeeId,
    sessionId,
    date,
    startDate,
    endDate,
    page = 1,
    limit = 60,
  } = {}) => {
    const params = { page, limit };
    if (employeeId) params.employeeId = employeeId;
    if (sessionId) params.sessionId = sessionId;
    if (date) params.date = date;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const res = await axiosPrivate.get("/api/monitoring/admin/screenshots", {
      params,
    });
    return res.data;
  },

  createScreenshotToken: async (screenshotId, ttlSeconds) => {
    const res = await axiosPrivate.post(
      `/api/monitoring/admin/screenshots/${screenshotId}/playback-token`,
      ttlSeconds ? { ttlSeconds } : {},
    );
    return res.data;
  },

  /** Absolute url for an <img src>. Tokens are kind-scoped, so this cannot open a video. */
  getScreenshotUrl: (screenshotId, token) =>
    `${BASE_URL}/api/monitoring/screenshots/${screenshotId}/stream?t=${encodeURIComponent(token)}`,

  // ── Settings ──────────────────────────────────────────────────────────────
  getSettings: async () => {
    const res = await axiosPrivate.get("/api/monitoring/admin/settings");
    return res.data;
  },

  updateSettings: async (patch) => {
    const res = await axiosPrivate.put(
      "/api/monitoring/admin/settings",
      patch,
    );
    return res.data;
  },

  // ── Capture-mode overrides (video-on-demand) ──────────────────────────────
  getOverrides: async ({ employeeId, active, page = 1, limit = 50 } = {}) => {
    const params = { page, limit };
    if (employeeId) params.employeeId = employeeId;
    if (active !== undefined) params.active = active;
    const res = await axiosPrivate.get("/api/monitoring/admin/overrides", {
      params,
    });
    return res.data;
  },

  createOverride: async ({
    employeeId,
    captureMode,
    expiresAt,
    reason,
    fps,
    resolution,
    videoBitrateKbps,
  }) => {
    const body = { employeeId, captureMode, expiresAt, reason };
    if (fps) body.fps = fps;
    if (resolution) body.resolution = resolution;
    if (videoBitrateKbps) body.videoBitrateKbps = videoBitrateKbps;
    const res = await axiosPrivate.post(
      "/api/monitoring/admin/overrides",
      body,
    );
    return res.data;
  },

  revokeOverride: async (id) => {
    const res = await axiosPrivate.delete(
      `/api/monitoring/admin/overrides/${id}`,
    );
    return res.data;
  },

  /** What actually applies to one employee right now (global merged with override). */
  getEffectiveForEmployee: async (employeeId) => {
    const res = await axiosPrivate.get(
      `/api/monitoring/admin/overrides/effective/${employeeId}`,
    );
    return res.data;
  },
};
